/**
 * Who may open /client/*.
 *
 * The role alone is the wrong question. EPIC 54 P2 stopped inviteClientMember
 * from overwriting an existing account's role — a worker invited to manage
 * their employer's orders used to be silently turned into a client_user,
 * which took their own portal away from them — and preserving the role only
 * half-solves it: they then have a real membership and a gate that will not
 * let them through. A person who works on site AND manages their employer's
 * orders is one person with two relationships.
 *
 * So: the roles that always belong here pass immediately, and anyone else is
 * admitted on the strength of an actual client membership, which only the
 * server can confirm.
 */

import { Box, CircularProgress } from '@mui/material';
import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { listMyClientInvitations } from '../services/clientInvitations';

import { useAuth } from './AuthContext';
import { homeForRole } from './roleHome';
import { isSignedIn } from './signedIn';

/** Roles whose home this is; everyone else needs a membership. */
const ALLOWED: ReadonlyArray<string> = ['client_user', 'admin'];

type Verdict = 'checking' | 'allowed' | 'denied';

export function RequireClientUser({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  const [byMembership, setByMembership] = useState<Verdict>('checking');

  const roleAllowed = !!user?.role && ALLOWED.includes(user.role);

  useEffect(() => {
    let cancelled = false;
    if (!isSignedIn(user) || roleAllowed) {
      setByMembership('checking');
      return;
    }
    void (async () => {
      try {
        const result = await listMyClientInvitations();
        // An invitation counts too: somebody has to be able to reach the
        // page that accepts it.
        const has = result.hasActive || result.pending > 0;
        if (!cancelled) setByMembership(has ? 'allowed' : 'denied');
      } catch {
        if (!cancelled) setByMembership('denied');
      }
    })();
    return () => { cancelled = true; };
  }, [user, roleAllowed]);

  const spinner = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
      <CircularProgress />
    </Box>
  );

  if (loading) return spinner;

  // An anonymous guest session (/order) is not an account — sign in first.
  if (!isSignedIn(user)) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (roleAllowed) return <>{children}</>;

  if (byMembership === 'checking') return spinner;
  if (byMembership === 'denied') return <Navigate to={homeForRole(user.role)} replace />;

  return <>{children}</>;
}
